import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { clearAllDrafts } from "../utils/draftStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Emulation state (read-only view-as) ─────────────────────────────
  const [emulatedUserId, setEmulatedUserId] = useState(null);
  const isEmulating = !!emulatedUserId;
  const originalFetchRef = useRef(null);

  // ── Act-as-delegate state (real writes, attributed on behalf of) ────
  const [actAsUserId, setActAsUserId] = useState(null);
  const isActingAs = !!actAsUserId;
  const actAsOriginalFetchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const res = await fetch("/api/auth/me");
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setUser(data);
        } else {
          if (!cancelled) setUser(null);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  // ── Emulation fetch interceptor ───────────────────────────────────
  // When emulating, monkey-patch window.fetch to inject the header.
  useEffect(() => {
    if (!emulatedUserId) {
      // Restore original fetch if emulation stopped
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
        originalFetchRef.current = null;
      }
      return;
    }

    // Save original fetch
    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch;
    }
    const original = originalFetchRef.current;

    window.fetch = (url, opts = {}) => {
      const headers = new Headers(opts.headers || {});
      headers.set("X-Emulate-User", emulatedUserId);
      return original(url, { ...opts, headers });
    };

    return () => {
      // Cleanup on unmount
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
        originalFetchRef.current = null;
      }
    };
  }, [emulatedUserId]);

  // ── Act-as fetch interceptor ────────────────────────────────────────
  // Independent of the emulation interceptor above — the two are mutually
  // exclusive in the backend (X-Emulate-User takes precedence if both were
  // ever somehow set), but keeping the fetch patch separate avoids coupling
  // this new write-enabled mode to the read-only emulation code path.
  useEffect(() => {
    if (!actAsUserId) {
      if (actAsOriginalFetchRef.current) {
        window.fetch = actAsOriginalFetchRef.current;
        actAsOriginalFetchRef.current = null;
      }
      return;
    }

    if (!actAsOriginalFetchRef.current) {
      actAsOriginalFetchRef.current = window.fetch;
    }
    const original = actAsOriginalFetchRef.current;

    window.fetch = (url, opts = {}) => {
      const headers = new Headers(opts.headers || {});
      headers.set("X-Act-As-User", actAsUserId);
      return original(url, { ...opts, headers });
    };

    return () => {
      if (actAsOriginalFetchRef.current) {
        window.fetch = actAsOriginalFetchRef.current;
        actAsOriginalFetchRef.current = null;
      }
    };
  }, [actAsUserId]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me");
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return data;
      } else {
        setUser(null);
        return null;
      }
    } catch {
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    // Stop emulation / act-as first if active
    if (originalFetchRef.current) {
      window.fetch = originalFetchRef.current;
      originalFetchRef.current = null;
    }
    setEmulatedUserId(null);
    if (actAsOriginalFetchRef.current) {
      window.fetch = actAsOriginalFetchRef.current;
      actAsOriginalFetchRef.current = null;
    }
    setActAsUserId(null);

    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // ignore
    }
    clearAllDrafts();
    setUser(null);
    window.location.href = import.meta.env.BASE_URL + "login";
  }, []);

  // ── Emulation controls ────────────────────────────────────────────
  const startEmulation = useCallback(async (userId) => {
    setEmulatedUserId(userId);

    // We need to manually fetch /me with the emulation header since
    // the fetch interceptor effect may not have run yet.
    const original = originalFetchRef.current || window.fetch;
    try {
      const res = await original("/api/auth/me", {
        headers: { "X-Emulate-User": userId },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch {
      // If fetch fails, revert emulation
      setEmulatedUserId(null);
    }
  }, []);

  const stopEmulation = useCallback(async () => {
    // Restore original fetch first
    if (originalFetchRef.current) {
      window.fetch = originalFetchRef.current;
      originalFetchRef.current = null;
    }
    setEmulatedUserId(null);

    // Re-fetch real user data (using the now-restored fetch)
    try {
      const res = await fetch("/api/auth/me");
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch {
      // ignore
    }
  }, []);

  // ── Act-as-delegate controls ─────────────────────────────────────
  const startActingAs = useCallback(async (userId) => {
    setActAsUserId(userId);
    const original = actAsOriginalFetchRef.current || window.fetch;
    try {
      const res = await original("/api/auth/me", {
        headers: { "X-Act-As-User": userId },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch {
      setActAsUserId(null);
    }
  }, []);

  const stopActingAs = useCallback(async () => {
    if (actAsOriginalFetchRef.current) {
      window.fetch = actAsOriginalFetchRef.current;
      actAsOriginalFetchRef.current = null;
    }
    setActAsUserId(null);
    try {
      const res = await fetch("/api/auth/me");
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch {
      // ignore
    }
  }, []);

  // ── Permission-based checks (new RBAC) ──────────────────────────
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      const perms = user.permissions || [];
      return perms.includes(permission);
    },
    [user]
  );

  // ── Legacy role-based checks (backward compat) ──────────────────
  const hasRole = useCallback(
    (...roles) => user && roles.includes(user.role),
    [user]
  );

  const isAdmin = useCallback(
    () => hasPermission("admin.settings") || hasRole("admin"),
    [hasPermission, hasRole]
  );
  const isManagerOrAdmin = useCallback(
    () => hasPermission("leads.manual_screen_2") || hasRole("admin", "manager"),
    [hasPermission, hasRole]
  );
  const isAnalystPlus = useCallback(
    () => hasPermission("leads.view") || hasRole("admin", "manager", "analyst"),
    [hasPermission, hasRole]
  );

  const canAccessRd = useCallback(
    (permission = "rd.view") => {
      if (hasPermission(permission)) return true;
      const roleName = user?.role_name || user?.role || "";
      return ["Admin", "Capture Manager", "admin", "manager"].includes(roleName);
    },
    [hasPermission, user]
  );

  // True when user is authenticated but has no role assigned yet
  const isPending = !!user && user.pending === true;

  return (
    <AuthContext.Provider
      value={{
        user, loading, logout, refreshUser,
        isPending,
        hasRole, hasPermission, canAccessRd, isAdmin, isManagerOrAdmin, isAnalystPlus,
        // Emulation
        isEmulating, emulatedUserId, startEmulation, stopEmulation,
        // Act-as-delegate
        isActingAs, actAsUserId, startActingAs, stopActingAs,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
